import { graphqlRaw } from '../graphql/client.ts';
import { type UserContext } from './config.ts';
import { error, hint } from './output.ts';

/**
 * A company id is the url-safe base64 of `Company/<uuid>`; anything else a
 * person types for a company is taken as its slug.
 */
export function isCompanyId(value: string): boolean {
  try {
    return Buffer.from(value, 'base64url').toString('utf8').startsWith('Company/');
  } catch {
    return false;
  }
}

const COMPANY_BY_SLUG = `query CompanyBySlug($slug: String!) { company(slug: $slug) { id } }`;

const resolved = new Map<string, Promise<string>>();

async function lookupCompanyId(slug: string): Promise<string> {
  const data = await graphqlRaw(COMPANY_BY_SLUG, { slug }, { withoutCompany: true });
  const id = (data.company as { id?: string } | null)?.id;
  if (typeof id === 'string') return id;

  error(`No company with slug "${slug}" among yours.`);
  hint('`sl companies` lists the companies you belong to, with their slugs and ids.');
  process.exit(2);
}

/**
 * Resolve the company a request acts for to its id, from the `--company` flag
 * first and `config.company` second. A slug costs one lookup per process.
 */
export async function resolveCompanyId(opts: {
  company?: string | undefined;
  ctx: UserContext | undefined;
}): Promise<string | undefined> {
  const value = opts.company ?? opts.ctx?.company;
  if (!value) return undefined;
  if (isCompanyId(value)) return value;

  let pending = resolved.get(value);
  if (!pending) {
    pending = lookupCompanyId(value);
    resolved.set(value, pending);
  }
  return pending;
}
